import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  projectModels: [],
};

function handleRequest(state: State) {
  return {
    ...state,
    request: { ...initialState.request, isPending: true },
  };
}

function handleSuccess(
  state: State,
  { projectModels }: Boundaries["retrieveProjectModels"]["success"],
): State {
  return {
    ...state,
    request: { ...initialState.request, isSuccessful: true },
    projectModels,
  };
}

function handleFailure(state: State) {
  return {
    ...state,
    request: { ...initialState.request, isFailure: true },
  };
}

function handleProjectRemoval(
  state: State,
  project: Boundaries["removeProject"]["success"],
): State {
  return {
    ...state,
    projectModels: state.projectModels.filter(
      ({ uuid }) => uuid !== project.uuid,
    ),
  };
}

function handleProjectCreation(
  state: State,
  { project }: Boundaries["createProject"]["success"],
): State {
  return {
    ...state,
    projectModels: project.isTemplate
      ? [...state.projectModels, project]
      : state.projectModels,
  };
}

function handleCreateProjectModelFromProject(
  state: State,
  { projectModel }: Boundaries["createProjectModelFromProject"]["success"],
): State {
  return {
    ...state,
    projectModels: [projectModel, ...state.projectModels],
  };
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    projectModels: state.projectModels.map((model) =>
      reduceProject(model, event),
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectModelsRequested]: handleRequest,
      [events.retrieveProjectModelsSucceeded]: handleSuccess,
      [events.retrieveProjectModelsFailed]: handleFailure,
      [events.removeProjectSucceeded]: handleProjectRemoval,
      [events.createProjectSucceeded]: handleProjectCreation,
      [events.createProjectModelFromProjectSucceeded]:
        handleCreateProjectModelFromProject,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
